import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Save, RotateCcw } from 'lucide-react';
import { useConfig } from '../contexts/ConfigContext';
import { db } from '../database/supabase';

const TABS = [
  { id: 'site', label: 'Site Info' },
  { id: 'hours', label: 'Office Hours' },
  { id: 'features', label: 'Features' },
  { id: 'colors', label: 'Colors' },
];

export default function SettingsManagement() {
  const {
    config,
    updateSiteInfo,
    updateFeatures,
    updateColors,
    resetToDefaults,
  } = useConfig();

  const [tab, setTab] = useState('site');
  const [site, setSite] = useState(config.site);
  const [features, setFeatures] = useState(config.features);
  const [colors, setColors] = useState(config.colors);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSite(config.site);
    setFeatures(config.features);
    setColors(config.colors);
  }, [config]);

  const syncToSupabase = async (nextConfig) => {
    try {
      await db.updateSetting('site_config', nextConfig, 'Full site configuration JSON');
    } catch (err) {
      // Local save still succeeds if remote sync is unavailable
      console.warn('Could not sync settings to Supabase:', err.message || err);
    }
  };

  const handleSaveSite = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      updateSiteInfo(site);
      await syncToSupabase({ ...config, site });
      toast.success('Site information saved');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFeatures = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      updateFeatures(features);
      await syncToSupabase({ ...config, features });
      toast.success('Feature flags saved');
    } catch (err) {
      toast.error('Failed to save features');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveColors = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      updateColors(colors);
      await syncToSupabase({ ...config, colors });
      toast.success('Colors saved');
    } catch (err) {
      toast.error('Failed to save colors');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!window.confirm('Reset all settings to defaults?')) return;
    resetToDefaults();
    toast.success('Settings reset to defaults');
  };

  const setNested = (setter, path, value) => {
    setter((prev) => {
      const next = { ...prev };
      const parts = path.split('.');
      let cursor = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cursor[parts[i]] = { ...cursor[parts[i]] };
        cursor = cursor[parts[i]];
      }
      cursor[parts[parts.length - 1]] = value;
      return next;
    });
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600">Configure site information, features, and appearance</p>
        </div>
        <button type="button" onClick={handleReset} className="btn-outline inline-flex items-center">
          <RotateCcw className="w-4 h-4 mr-2" />
          Reset Defaults
        </button>
      </div>

      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex flex-wrap gap-4">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`pb-3 px-1 border-b-2 text-sm font-medium ${
                tab === t.id
                  ? 'border-blue-800 text-blue-800'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'site' && (
        <form onSubmit={handleSaveSite} className="bg-white rounded-lg shadow p-6 space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Center Name</label>
              <input
                className="form-input"
                value={site.name || ''}
                onChange={(e) => setSite({ ...site, name: e.target.value })}
              />
            </div>
            <div>
              <label className="form-label">School Name</label>
              <input
                className="form-input"
                value={site.schoolName || ''}
                onChange={(e) => setSite({ ...site, schoolName: e.target.value })}
              />
            </div>
            <div>
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-input"
                value={site.email || ''}
                onChange={(e) => setSite({ ...site, email: e.target.value })}
              />
            </div>
            <div>
              <label className="form-label">Phone</label>
              <input
                className="form-input"
                value={site.phone || ''}
                onChange={(e) => setSite({ ...site, phone: e.target.value })}
              />
            </div>
          </div>

          <div>
            <h3 className="section-subtitle">Address</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="form-label">Street</label>
                <input
                  className="form-input"
                  value={site.address?.street || ''}
                  onChange={(e) => setNested(setSite, 'address.street', e.target.value)}
                />
              </div>
              <div>
                <label className="form-label">City</label>
                <input
                  className="form-input"
                  value={site.address?.city || ''}
                  onChange={(e) => setNested(setSite, 'address.city', e.target.value)}
                />
              </div>
              <div>
                <label className="form-label">State / Province</label>
                <input
                  className="form-input"
                  value={site.address?.state || ''}
                  onChange={(e) => setNested(setSite, 'address.state', e.target.value)}
                />
              </div>
              <div>
                <label className="form-label">ZIP</label>
                <input
                  className="form-input"
                  value={site.address?.zip || ''}
                  onChange={(e) => setNested(setSite, 'address.zip', e.target.value)}
                />
              </div>
              <div>
                <label className="form-label">Country</label>
                <input
                  className="form-input"
                  value={site.address?.country || ''}
                  onChange={(e) => setNested(setSite, 'address.country', e.target.value)}
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="section-subtitle">Social Links</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {['facebook', 'twitter', 'instagram', 'website'].map((key) => (
                <div key={key}>
                  <label className="form-label capitalize">{key}</label>
                  <input
                    className="form-input"
                    value={site.social?.[key] || ''}
                    onChange={(e) => setNested(setSite, `social.${key}`, e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <button type="submit" disabled={saving} className="btn-primary inline-flex items-center">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Site Info'}
            </button>
          </div>
        </form>
      )}

      {tab === 'hours' && (
        <form onSubmit={handleSaveSite} className="bg-white rounded-lg shadow p-6 space-y-4">
          {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(
            (day) => (
              <div key={day} className="grid md:grid-cols-3 gap-4 items-center">
                <label className="form-label capitalize mb-0">{day}</label>
                <input
                  className="form-input md:col-span-2"
                  value={site.hours?.[day] || ''}
                  onChange={(e) => setNested(setSite, `hours.${day}`, e.target.value)}
                />
              </div>
            )
          )}
          <div className="flex justify-end pt-2">
            <button type="submit" disabled={saving} className="btn-primary inline-flex items-center">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Hours'}
            </button>
          </div>
        </form>
      )}

      {tab === 'features' && (
        <form onSubmit={handleSaveFeatures} className="bg-white rounded-lg shadow p-6">
          <div className="grid md:grid-cols-2 gap-4">
            {Object.keys(features).map((key) => (
              <label
                key={key}
                className="flex items-center justify-between gap-3 border border-gray-200 rounded-lg px-4 py-3"
              >
                <span className="text-sm text-gray-700">
                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())}
                </span>
                <input
                  type="checkbox"
                  checked={!!features[key]}
                  onChange={(e) => setFeatures({ ...features, [key]: e.target.checked })}
                />
              </label>
            ))}
          </div>
          <div className="flex justify-end mt-6">
            <button type="submit" disabled={saving} className="btn-primary inline-flex items-center">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Features'}
            </button>
          </div>
        </form>
      )}

      {tab === 'colors' && (
        <form onSubmit={handleSaveColors} className="bg-white rounded-lg shadow p-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(colors).map(([key, value]) => (
              <div key={key}>
                <label className="form-label capitalize">
                  {key.replace(/([A-Z])/g, ' $1')}
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    className="h-11 w-14 border border-gray-300 rounded cursor-pointer"
                    value={/^#[0-9A-Fa-f]{6}$/.test(value) ? value : '#000000'}
                    onChange={(e) => setColors({ ...colors, [key]: e.target.value })}
                  />
                  <input
                    className="form-input"
                    value={value}
                    onChange={(e) => setColors({ ...colors, [key]: e.target.value })}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end mt-6">
            <button type="submit" disabled={saving} className="btn-primary inline-flex items-center">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Colors'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
